/**
 * Frontline — territory paint + lane spawn + kill evolution + card/mana skills.
 * Phaser-free.
 */

import { frontlineCatalog } from "../../data/frontline-catalog";
import {
  SpatialHash2D,
  applySoftSepSpatial,
  retreatBlockedAtEdge as retreatBlockedAtEdgeRect,
} from "../../sim";
import {
  createTeamCards,
  ensureDeck,
  handHas,
  pickAiCard,
  refundCard,
  spendCard,
  tickManaAndDraw,
  type FlSkillId,
  type TeamCards,
} from "./frontline-cards";
import { FlEvent, type FlSimEvent } from "./frontline-events";

export type { FlSkillId, TeamCards } from "./frontline-cards";

const C = frontlineCatalog.balance;
const AB = frontlineCatalog.abilities;
const EVO = frontlineCatalog.evolution;

const SK = frontlineCatalog.skills;
const CARDS = SK.cards;

export type TeamId = 0 | 1;
export type FlTier = 1 | 2 | 3;

export type FlUnit = {
  id: number;
  team: TeamId;
  tier: FlTier;
  unitId: string;
  /** T1 role that defines promote lineage. */
  lineage: string;
  kills: number;
  level: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  intentVx: number;
  intentVy: number;
  arrived: boolean;
  speed: number;
  baseSpeed: number;
  facing: number;
  hp: number;
  maxHp: number;
  damage: number;
  radius: number;
  attackRange: number;
  attackCdMax: number;
  attackCd: number;
  abilityCd: number;
  aimAngle: number;
  /** Remaining melee swing presentation time (sec). */
  attackAnimT: number;
  /** Temporary invulnerability from aegis card. */
  aegisT: number;
  surgeT: number;
  stunT: number;
  /** Elevate card used once (blocks repeat force-promote). */
  elevateUsed: boolean;
};

/** Reused across sep ticks — cleared/rebuilt each frame. */
const sepHash = new SpatialHash2D<FlUnit>();
const sepNear: FlUnit[] = [];

export type FrontlinePhase = "playing" | "won" | "lost" | "draw";

export type DeployFail =
  | "none_selected"
  | "cooldown"
  | "out_of_bounds"
  | "not_owned"
  | "hq_cell"
  | "busy"
  | "no_target"
  | "wrong_target"
  | "no_mana"
  | "not_in_hand";

export type PendingAoeWave = {
  skillId: FlSkillId;
  team: TeamId;
  originCol: number;
  originRow: number;
  cellRange: number;
  /** Next Chebyshev ring to apply (0 = origin cell). */
  nextRing: number;
  /** Seconds until nextRing resolves. */
  cd: number;
};

export type FrontlineState = {
  time: number;
  timeLeft: number;
  phase: FrontlinePhase;
  cols: number;
  rows: number;
  cellW: number;
  cellH: number;
  originX: number;
  originY: number;
  /** -1 neutral, 0 ally, 1 enemy */
  owner: Int8Array;
  stamp: Float64Array;
  units: FlUnit[];
  nextId: number;
  baseHp: [number, number];
  baseMaxHp: [number, number];
  /** Currently selected hand card (player). */
  selectedSkill: FlSkillId | null;
  /** Hand slot index for the selected card (duplicate-safe). */
  selectedHandIndex: number | null;
  cards: [TeamCards, TeamCards];
  deployCd: number;
  /** Lane wave timers per team. */
  laneCd: [number, number];
  /** Deterministic RNG seed state (mulberry32). */
  rng: number;
  events: FlSimEvent[];
  /** Cell-based AoE waves in progress (center → outer rings). */
  aoeWaves: PendingAoeWave[];
};

export type HudSnapshot = {
  timeLeft: number;
  phase: FrontlinePhase;
  selectedSkill: FlSkillId | null;
  selectedHandIndex: number | null;
  hand: FlSkillId[];
  mana: number;
  maxMana: number;
  deckCount: number;
  discardCount: number;
  exileCount: number;
  cardCosts: Record<FlSkillId, number>;
  cardExile: Record<FlSkillId, boolean>;
  deployCd: number;
  baseHp: [number, number];
  baseMaxHp: [number, number];
  ownedAlly: number;
  ownedEnemy: number;
  unitCount: [number, number];
  topLevel: number;
  eliteCount: [number, number];
};

function cellIndex(cols: number, col: number, row: number): number {
  return row * cols + col;
}

export function worldToCell(
  state: FrontlineState,
  x: number,
  y: number,
): { col: number; row: number } | null {
  const col = Math.floor((x - state.originX) / state.cellW);
  const row = Math.floor((y - state.originY) / state.cellH);
  if (col < 0 || row < 0 || col >= state.cols || row >= state.rows) return null;
  return { col, row };
}

export function cellCenter(
  state: FrontlineState,
  col: number,
  row: number,
): { x: number; y: number } {
  return {
    x: state.originX + (col + 0.5) * state.cellW,
    y: state.originY + (row + 0.5) * state.cellH,
  };
}

/** Apply fitted board metrics; remaps living units to the same cell centers. */
export function applyFrontlineBoardMetrics(
  state: FrontlineState,
  metrics: {
    originX: number;
    originY: number;
    cellW: number;
    cellH: number;
  },
): void {
  const mapped = state.units.map((u) => {
    const cell = worldToCell(state, u.x, u.y);
    return { u, cell };
  });
  state.originX = metrics.originX;
  state.originY = metrics.originY;
  state.cellW = metrics.cellW;
  state.cellH = metrics.cellH;
  for (const { u, cell } of mapped) {
    if (!cell) continue;
    const c = cellCenter(state, cell.col, cell.row);
    u.x = c.x;
    u.y = c.y;
  }
}

/** Top-left col of centered HQ footprint. */
export function hqOriginCol(state: FrontlineState): number {
  return Math.floor((state.cols - C.hqFootprint.cols) / 2);
}

/** Top-left row of HQ footprint for the team that owns that base. */
export function hqOriginRow(state: FrontlineState, owner: TeamId): number {
  if (owner === 1) return 0;
  return state.rows - C.hqFootprint.rows;
}

export function isHqCell(
  state: FrontlineState,
  col: number,
  row: number,
): boolean {
  const oc = hqOriginCol(state);
  for (const owner of [0, 1] as const) {
    const or = hqOriginRow(state, owner);
    if (
      col >= oc &&
      col < oc + C.hqFootprint.cols &&
      row >= or &&
      row < or + C.hqFootprint.rows
    ) {
      return true;
    }
  }
  return false;
}

/** World center of the base owned by `owner` (footprint centroid). */
export function baseCenter(
  state: FrontlineState,
  owner: TeamId,
): { x: number; y: number } {
  const oc = hqOriginCol(state);
  const or = hqOriginRow(state, owner);
  const c0 = cellCenter(state, oc, or);
  const c1 = cellCenter(
    state,
    oc + C.hqFootprint.cols - 1,
    or + C.hqFootprint.rows - 1,
  );
  return { x: (c0.x + c1.x) * 0.5, y: (c0.y + c1.y) * 0.5 };
}

function nextRng(state: FrontlineState): number {
  let t = (state.rng += 0x6d2b79f5);
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

export function pickFromPool(state: FrontlineState, tier: FlTier): string {
  const pool =
    tier === 1 ? C.pools.t1 : tier === 2 ? C.pools.t2 : C.pools.t3;
  const i = Math.floor(nextRng(state) * pool.length) % pool.length;
  return pool[i]!;
}

function tierKey(tier: FlTier): "1" | "2" | "3" {
  return String(tier) as "1" | "2" | "3";
}

export function statsForUnit(
  tier: FlTier,
  unitId: string,
): {
  hp: number;
  damage: number;
  radius: number;
  speed: number;
  attackRange: number;
  attackCd: number;
} {
  const t = C.tiers[tierKey(tier)];
  const role = (
    C.roles as Record<
      string,
      {
        attackRange?: number;
        attackCd?: number;
        damageMul?: number;
        speed?: number;
        radius?: number;
      }
    >
  )[unitId] ?? {};
  return {
    hp: t.hp,
    damage: Math.round(t.damage * (role.damageMul ?? 1)),
    radius: role.radius ?? t.radius,
    speed: role.speed ?? t.speed,
    attackRange: role.attackRange ?? t.attackRange,
    attackCd: role.attackCd ?? t.attackCd,
  };
}

export function statsForTier(tier: FlTier): {
  hp: number;
  damage: number;
  radius: number;
  speed: number;
} {
  const t = C.tiers[tierKey(tier)];
  return {
    hp: t.hp,
    damage: t.damage,
    radius: t.radius,
    speed: t.speed,
  };
}

export function displayScaleForTier(tier: FlTier): number {
  return C.tiers[tierKey(tier)].display;
}

/** @deprecated stack display removed — use displayScaleForTier */
export function displayScaleForStack(_stack: number): number {
  return 1;
}

export function attackRangeOf(u: FlUnit): number {
  return u.attackRange;
}

export function createFrontlineState(seed = 0xc0ffee): FrontlineState {
  const cols = C.cols;
  const rows = C.rows;
  const n = cols * rows;
  const owner = new Int8Array(n);
  const stamp = new Float64Array(n);
  owner.fill(-1);

  const playerRows = C.playerBaseRows;
  const enemyRows = C.enemyBaseRows;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const i = cellIndex(cols, c, r);
      if (r >= rows - playerRows) {
        owner[i] = 0;
        stamp[i] = 0;
      } else if (r < enemyRows) {
        owner[i] = 1;
        stamp[i] = 0;
      }
    }
  }

  const hp = C.baseHp;
  const state: FrontlineState = {
    time: 0,
    timeLeft: C.matchSeconds,
    phase: "playing",
    cols,
    rows,
    cellW: C.cellW,
    cellH: C.cellH,
    originX: C.originX,
    originY: C.originY,
    owner,
    stamp,
    units: [],
    nextId: 1,
    baseHp: [hp, hp],
    baseMaxHp: [hp, hp],
    selectedSkill: null,
    selectedHandIndex: null,
    cards: [
      {
        mana: SK.economy.startMana,
        deck: [],
        hand: [],
        discard: [],
        exiled: [],
        drawCd: SK.economy.drawIntervalSec,
        aiCastCd: SK.ai.castIntervalSec,
      },
      {
        mana: SK.economy.startMana,
        deck: [],
        hand: [],
        discard: [],
        exiled: [],
        drawCd: SK.economy.drawIntervalSec,
        aiCastCd: SK.ai.castIntervalSec * 0.5,
      },
    ],
    deployCd: 0,
    laneCd: [C.lane.intervalSec * 0.35, C.lane.intervalSec * 0.55],
    rng: seed >>> 0,
    events: [],
    aoeWaves: [],
  };
  const rng = { next: () => nextRng(state) };
  state.cards[0] = createTeamCards(rng);
  state.cards[1] = createTeamCards(rng);
  return state;
}

export function selectHandIndex(
  state: FrontlineState,
  index: number | null,
): void {
  if (state.phase !== "playing") return;
  if (index === null) {
    state.selectedSkill = null;
    state.selectedHandIndex = null;
    return;
  }
  const id = state.cards[0]!.hand[index];
  if (id == null) return;
  if (state.selectedHandIndex === index) {
    state.selectedSkill = null;
    state.selectedHandIndex = null;
    return;
  }
  state.selectedHandIndex = index;
  state.selectedSkill = id;
}

/** Select by skill id (first matching hand slot). Prefer selectHandIndex for UI. */
export function selectSkill(state: FrontlineState, skill: FlSkillId | null): void {
  if (state.phase !== "playing") return;
  if (skill === null) {
    state.selectedSkill = null;
    state.selectedHandIndex = null;
    return;
  }
  const idx = state.cards[0]!.hand.indexOf(skill);
  if (idx < 0) return;
  if (state.selectedHandIndex === idx && state.selectedSkill === skill) {
    state.selectedSkill = null;
    state.selectedHandIndex = null;
    return;
  }
  state.selectedHandIndex = idx;
  state.selectedSkill = skill;
}

/** @deprecated use selectHandIndex / selectSkill */
export function toggleSelect(state: FrontlineState): void {
  selectSkill(state, state.selectedSkill);
}

export function canDeployAt(
  state: FrontlineState,
  team: TeamId,
  col: number,
  row: number,
): boolean {
  if (col < 0 || row < 0 || col >= state.cols || row >= state.rows) return false;
  if (isHqCell(state, col, row)) return false;
  return state.owner[cellIndex(state.cols, col, row)] === team;
}

function spawnUnit(
  state: FrontlineState,
  team: TeamId,
  x: number,
  y: number,
  tier: FlTier,
  unitId: string,
  lineage?: string,
): FlUnit {
  const st = statsForUnit(tier, unitId);
  const ab = (AB as Record<string, { cd?: number } | undefined>)[unitId];
  const lin =
    lineage ??
    (tier === 1
      ? unitId
      : Object.entries(EVO.lineage).find(([, v]) => {
          const t2 = asRolePool(v.t2);
          const t3 = asRolePool(v.t3);
          return t2.includes(unitId) || t3.includes(unitId);
        })?.[0] ?? unitId);
  const u: FlUnit = {
    id: state.nextId++,
    team,
    tier,
    unitId,
    lineage: lin,
    kills: 0,
    level: 1,
    x,
    y,
    vx: 0,
    vy: 0,
    intentVx: 0,
    intentVy: 0,
    arrived: false,
    speed: st.speed,
    baseSpeed: st.speed,
    facing: team === 0 ? -1 : 1,
    hp: st.hp,
    maxHp: st.hp,
    damage: st.damage,
    radius: st.radius,
    attackRange: st.attackRange,
    attackCdMax: st.attackCd,
    attackCd: 0,
    abilityCd: ab?.cd ? ab.cd * 0.35 : 0,
    aimAngle: team === 0 ? -Math.PI / 2 : Math.PI / 2,
    attackAnimT: 0,
    aegisT: 0,
    surgeT: 0,
    stunT: 0,
    elevateUsed: false,
  };
  state.units.push(u);
  return u;
}

function emitSkill(
  state: FrontlineState,
  skillId: FlSkillId,
  team: TeamId,
  x: number,
  y: number,
  opts?: { targetId?: number; heal?: number; damage?: number },
): void {
  state.events.push({
    kind: FlEvent.Skill,
    skillId,
    team,
    x,
    y,
    targetId: opts?.targetId,
    heal: opts?.heal,
    damage: opts?.damage,
  });
}

function chebyshev(
  colA: number,
  rowA: number,
  colB: number,
  rowB: number,
): number {
  return Math.max(Math.abs(colA - colB), Math.abs(rowA - rowB));
}

export function cardCellRange(skillId: FlSkillId): number {
  const def = CARDS[skillId] as { cellRange?: number } | undefined;
  return typeof def?.cellRange === "number" ? def.cellRange : 1;
}

/** Cells in Chebyshev footprint (inclusive). */
export function cellsInChebyshevRange(
  state: FrontlineState,
  originCol: number,
  originRow: number,
  cellRange: number,
): { col: number; row: number }[] {
  const out: { col: number; row: number }[] = [];
  for (let r = originRow - cellRange; r <= originRow + cellRange; r++) {
    for (let c = originCol - cellRange; c <= originCol + cellRange; c++) {
      if (c < 0 || r < 0 || c >= state.cols || r >= state.rows) continue;
      if (chebyshev(c, r, originCol, originRow) > cellRange) continue;
      out.push({ col: c, row: r });
    }
  }
  return out;
}

export function unitsInCellRange(
  state: FrontlineState,
  side: "ally" | "enemy",
  team: TeamId,
  originCol: number,
  originRow: number,
  cellRange: number,
  ringExact?: number,
): FlUnit[] {
  return state.units.filter((u) => {
    if (u.hp <= 0) return false;
    if (side === "ally" && u.team !== team) return false;
    if (side === "enemy" && u.team === team) return false;
    const cell = worldToCell(state, u.x, u.y);
    if (!cell) return false;
    const d = chebyshev(cell.col, cell.row, originCol, originRow);
    if (ringExact != null) return d === ringExact;
    return d <= cellRange;
  });
}

function aoeSideForSkill(sid: FlSkillId): "ally" | "enemy" {
  return sid === "smite" || sid === "stun" ? "enemy" : "ally";
}

function applyAoeRing(
  state: FrontlineState,
  wave: PendingAoeWave,
  ring: number,
): void {
  const def = CARDS[wave.skillId] as Record<string, unknown>;
  const side = aoeSideForSkill(wave.skillId);
  const units = unitsInCellRange(
    state,
    side,
    wave.team,
    wave.originCol,
    wave.originRow,
    wave.cellRange,
    ring,
  );

  const cells: { col: number; row: number; x: number; y: number }[] = [];
  for (let r = wave.originRow - ring; r <= wave.originRow + ring; r++) {
    for (let c = wave.originCol - ring; c <= wave.originCol + ring; c++) {
      if (c < 0 || r < 0 || c >= state.cols || r >= state.rows) continue;
      if (chebyshev(c, r, wave.originCol, wave.originRow) !== ring) continue;
      const ctr = cellCenter(state, c, r);
      cells.push({ col: c, row: r, x: ctr.x, y: ctr.y });
    }
  }

  const hits: {
    unitId: number;
    x: number;
    y: number;
    heal?: number;
    damage?: number;
  }[] = [];

  if (wave.skillId === "smite") {
    const dmg = cardNum(def, "damage", 220);
    for (const foe of units) {
      applyRawDamage(state, foe, dmg);
      hits.push({ unitId: foe.id, x: foe.x, y: foe.y, damage: dmg });
    }
  } else if (wave.skillId === "stun") {
    const dur = cardNum(def, "duration", 1.5);
    for (const foe of units) {
      foe.stunT = Math.max(foe.stunT, dur);
      hits.push({ unitId: foe.id, x: foe.x, y: foe.y });
    }
  } else if (wave.skillId === "mend") {
    const heal = cardNum(def, "heal", 280);
    for (const a of units) {
      const before = a.hp;
      a.hp = Math.min(a.maxHp, a.hp + heal);
      hits.push({
        unitId: a.id,
        x: a.x,
        y: a.y,
        heal: Math.max(0, a.hp - before),
      });
    }
  } else if (wave.skillId === "aegis") {
    const dur = cardNum(def, "duration", 2);
    for (const a of units) {
      a.aegisT = Math.max(a.aegisT, dur);
      hits.push({ unitId: a.id, x: a.x, y: a.y });
    }
  } else if (wave.skillId === "surge") {
    const speedMul = cardNum(def, "speedMul", 1.25);
    const dur = cardNum(def, "duration", 2.5);
    for (const a of units) {
      a.surgeT = Math.max(a.surgeT, dur);
      a.speed = a.baseSpeed * speedMul;
      hits.push({ unitId: a.id, x: a.x, y: a.y });
    }
  } else if (wave.skillId === "elevate") {
    for (const a of units) {
      if (a.elevateUsed || a.tier >= 3) continue;
      const to = (a.tier + 1) as 2 | 3;
      promoteUnit(state, a, to);
      a.elevateUsed = true;
      hits.push({ unitId: a.id, x: a.x, y: a.y });
    }
  }

  state.events.push({
    kind: FlEvent.SkillWave,
    skillId: wave.skillId,
    team: wave.team,
    ring,
    cells,
    hits,
  });
}

function startAoeWave(
  state: FrontlineState,
  team: TeamId,
  sid: FlSkillId,
  x: number,
  y: number,
): { ok: true } | { ok: false; reason: DeployFail } {
  const cell = worldToCell(state, x, y);
  if (!cell) return { ok: false, reason: "out_of_bounds" };
  const cellRange = cardCellRange(sid);
  const side = aoeSideForSkill(sid);
  let candidates = unitsInCellRange(
    state,
    side,
    team,
    cell.col,
    cell.row,
    cellRange,
  );
  if (sid === "elevate") {
    candidates = candidates.filter((a) => !a.elevateUsed && a.tier < 3);
  }
  if (candidates.length === 0) return { ok: false, reason: "no_target" };

  const c = cellCenter(state, cell.col, cell.row);
  emitSkill(state, sid, team, c.x, c.y);

  const wave: PendingAoeWave = {
    skillId: sid,
    team,
    originCol: cell.col,
    originRow: cell.row,
    cellRange,
    nextRing: 0,
    cd: 0,
  };
  // Ring 0 resolves immediately so center targets feel responsive.
  applyAoeRing(state, wave, 0);
  wave.nextRing = 1;
  wave.cd =
    typeof (SK as { aoeRingIntervalSec?: number }).aoeRingIntervalSec ===
    "number"
      ? (SK as { aoeRingIntervalSec: number }).aoeRingIntervalSec
      : 0.12;
  if (wave.nextRing <= wave.cellRange) {
    state.aoeWaves.push(wave);
  }
  return { ok: true };
}

function tickAoeWaves(state: FrontlineState, dt: number): void {
  const interval =
    typeof (SK as { aoeRingIntervalSec?: number }).aoeRingIntervalSec ===
    "number"
      ? (SK as { aoeRingIntervalSec: number }).aoeRingIntervalSec
      : 0.12;
  const keep: PendingAoeWave[] = [];
  for (const wave of state.aoeWaves) {
    wave.cd -= dt;
    while (wave.cd <= 0 && wave.nextRing <= wave.cellRange) {
      applyAoeRing(state, wave, wave.nextRing);
      wave.nextRing += 1;
      wave.cd += interval;
    }
    if (wave.nextRing <= wave.cellRange) keep.push(wave);
  }
  state.aoeWaves = keep;
}

function deployAtCell(
  state: FrontlineState,
  team: TeamId,
  x: number,
  y: number,
  tier: FlTier,
  unitId: string,
  lineage?: string,
): FlUnit | null {
  const cell = worldToCell(state, x, y);
  if (!cell) return null;
  if (isHqCell(state, cell.col, cell.row)) return null;
  if (!canDeployAt(state, team, cell.col, cell.row)) return null;
  const c = cellCenter(state, cell.col, cell.row);
  const u = spawnUnit(state, team, c.x, c.y, tier, unitId, lineage);
  if (tier === 2) {
    u.kills = EVO.killsToT2;
    u.level = 1 + u.kills;
  }
  state.events.push({
    kind: FlEvent.Deploy,
    team,
    x: u.x,
    y: u.y,
    unitId: u.id,
    role: u.unitId,
    tier,
  });
  return u;
}

function cardNum(
  def: Record<string, unknown>,
  key: string,
  fallback: number,
): number {
  const v = def[key];
  return typeof v === "number" ? v : fallback;
}

/** Internal cast after mana/hand already validated (or AI). */
function applyCardEffect(
  state: FrontlineState,
  team: TeamId,
  sid: FlSkillId,
  x: number,
  y: number,
): { ok: true } | { ok: false; reason: DeployFail } {
  const def = CARDS[sid] as Record<string, unknown>;

  if (sid === "legion" || sid === "horde") {
    const cell = worldToCell(state, x, y);
    if (!cell) return { ok: false, reason: "out_of_bounds" };
    if (!canDeployAt(state, team, cell.col, cell.row)) {
      return { ok: false, reason: "not_owned" };
    }
    const c = cellCenter(state, cell.col, cell.row);
    const count = cardNum(def, "count", sid === "horde" ? 8 : 4);
    const rad = cardNum(def, "radius", sid === "horde" ? 52 : 40);
    let spawned = 0;
    for (let i = 0; i < count; i++) {
      const ang = (Math.PI * 2 * i) / count + state.time;
      const px = c.x + Math.cos(ang) * rad * 0.45;
      const py = c.y + Math.sin(ang) * rad * 0.45;
      const role = pickFromPool(state, 1);
      const u = spawnUnit(state, team, px, py, 1, role);
      state.events.push({
        kind: FlEvent.Deploy,
        team,
        x: px,
        y: py,
        unitId: u.id,
        role,
        tier: 1,
      });
      spawned++;
    }
    if (spawned === 0) return { ok: false, reason: "not_owned" };
    emitSkill(state, sid, team, c.x, c.y);
    return { ok: true };
  }

  if (sid === "champion") {
    const cell = worldToCell(state, x, y);
    if (!cell) return { ok: false, reason: "out_of_bounds" };
    if (!canDeployAt(state, team, cell.col, cell.row)) {
      return { ok: false, reason: "not_owned" };
    }
    const lineage = pickFromPool(state, 1);
    const lin = lineageEntry(lineage);
    if (!lin) return { ok: false, reason: "busy" };
    const role = pickFromRolePool(state, lin.t2);
    const u = deployAtCell(state, team, x, y, 2, role, lineage);
    if (!u) return { ok: false, reason: "not_owned" };
    emitSkill(state, sid, team, u.x, u.y, { targetId: u.id });
    return { ok: true };
  }

  if (sid === "titan") {
    const cell = worldToCell(state, x, y);
    if (!cell) return { ok: false, reason: "out_of_bounds" };
    if (!canDeployAt(state, team, cell.col, cell.row)) {
      return { ok: false, reason: "not_owned" };
    }
    const role = pickFromPool(state, 3);
    const lineage =
      Object.entries(EVO.lineage).find(([, v]) =>
        asRolePool(v.t3).includes(role),
      )?.[0] ?? "assault";
    const u = deployAtCell(state, team, x, y, 3, role, lineage);
    if (!u) return { ok: false, reason: "not_owned" };
    // Fresh T3 — kills stay 0 so soft-cap growth still has room.
    emitSkill(state, sid, team, u.x, u.y, { targetId: u.id });
    return { ok: true };
  }

  if (sid === "smite" || sid === "stun") {
    return startAoeWave(state, team, sid, x, y);
  }

  if (sid === "cataclysm") {
    // Field reset — wipe all units; HQ / territory stay. Exile card.
    const doomed = [...state.units];
    for (const u of doomed) {
      state.events.push({
        kind: FlEvent.Death,
        unitId: u.id,
        x: u.x,
        y: u.y,
      });
    }
    state.units = [];
    const mid = {
      x: state.originX + (state.cols * state.cellW) * 0.5,
      y: state.originY + (state.rows * state.cellH) * 0.5,
    };
    emitSkill(state, sid, team, mid.x, mid.y);
    return { ok: true };
  }

  if (
    sid === "mend" ||
    sid === "aegis" ||
    sid === "surge" ||
    sid === "elevate"
  ) {
    return startAoeWave(state, team, sid, x, y);
  }

  return { ok: false, reason: "busy" };
}

export function tryCastSkill(
  state: FrontlineState,
  team: TeamId,
  x: number,
  y: number,
  forcedSkill?: FlSkillId,
): { ok: true; skillId: FlSkillId } | { ok: false; reason: DeployFail } {
  if (state.phase !== "playing") return { ok: false, reason: "busy" };
  const sid =
    forcedSkill ??
    (team === 0 ? state.selectedSkill : null);
  if (!sid) return { ok: false, reason: "none_selected" };

  const bag = state.cards[team]!;
  const handIdx =
    team === 0 &&
    state.selectedHandIndex != null &&
    bag.hand[state.selectedHandIndex] === sid
      ? state.selectedHandIndex
      : undefined;
  const paid = spendCard(bag, sid, handIdx);
  if (!paid.ok) return { ok: false, reason: paid.reason };

  const applied = applyCardEffect(state, team, sid, x, y);
  if (!applied.ok) {
    refundCard(bag, sid, paid.exile);
    return applied;
  }

  ensureDeck(bag, { next: () => nextRng(state) });
  if (team === 0) {
    state.selectedSkill = null;
    state.selectedHandIndex = null;
  }
  return { ok: true, skillId: sid };
}

/** @deprecated player spawn is card-only — use tryCastSkill with legion/champion */
export function tryDeploy(
  state: FrontlineState,
  team: TeamId,
  x: number,
  y: number,
): { ok: true; unitId: number } | { ok: false; reason: DeployFail } {
  const r = tryCastSkill(state, team, x, y, "legion");
  if (!r.ok) return r;
  return { ok: true, unitId: 0 };
}

function paintFoot(state: FrontlineState, u: FlUnit): void {
  const cell = worldToCell(state, u.x, u.y);
  if (!cell) return;
  if (isHqCell(state, cell.col, cell.row)) return;
  const i = cellIndex(state.cols, cell.col, cell.row);
  state.owner[i] = u.team;
  state.stamp[i] = state.time;
}

function enemyBasePoint(state: FrontlineState, team: TeamId): { x: number; y: number } {
  const victim: TeamId = team === 0 ? 1 : 0;
  return baseCenter(state, victim);
}

function baseEngageRange(u: FlUnit): number {
  return u.attackRange + u.radius + C.baseRadius + C.baseEngagePad;
}

function holdEngage(u: FlUnit, aimX: number, aimY: number): void {
  u.intentVx = 0;
  u.intentVy = 0;
  u.vx *= 0.5;
  u.vy *= 0.5;
  u.aimAngle = Math.atan2(aimY - u.y, aimX - u.x);
  u.arrived = true;
}

function applySoftSep(state: FrontlineState, dt: number): void {
  const crowd = C.crowd;
  const cellHint = Math.max(
    24,
    crowd.sepRadius + crowd.sepRadiusMul * 28,
  );
  applySoftSepSpatial({
    items: state.units,
    hash: sepHash,
    cellSize: cellHint,
    getId: (u) => u.id,
    getXY: (u) => ({ x: u.x, y: u.y }),
    queryRadius: (a) =>
      crowd.sepRadius +
      crowd.sepRadiusMul * (a.radius * 2) +
      crowd.sepDisplayMul * 2,
    sepRadiusFor: (a, b) => {
      const dispA = displayScaleForTier(a.tier);
      const dispB = displayScaleForTier(b.tier);
      return (
        crowd.sepRadius +
        crowd.sepRadiusMul * (a.radius + b.radius) +
        crowd.sepDisplayMul * ((dispA + dispB) * 0.5 - 1)
      );
    },
    sepStrength: crowd.sepStrength,
    sepTopK: crowd.sepTopK,
    sepMaxImpulse: crowd.sepMaxImpulse,
    walkEnter: crowd.walkEnter,
    getIntent: (u) => ({ intentVx: u.intentVx, intentVy: u.intentVy }),
    onCoincide: (a, b) => {
      void b;
      const ang = (a.id * 2.399 + state.time) % (Math.PI * 2);
      const push = crowd.sepStrength;
      a.vx += Math.cos(ang) * push * dt * 0.5;
      a.vy += Math.sin(ang) * push * dt * 0.5;
    },
    extraForces: (a) => {
      let sx = 0;
      let sy = 0;
      for (const owner of [0, 1] as const) {
        const b = baseCenter(state, owner);
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        const d = Math.hypot(dx, dy);
        const minD = C.baseRadius + a.radius;
        if (d < 1e-4) {
          const ang = (a.id * 1.7 + owner) % (Math.PI * 2);
          sx += Math.cos(ang) * crowd.sepStrength;
          sy += Math.sin(ang) * crowd.sepStrength;
          continue;
        }
        if (d >= minD) continue;
        const push = ((minD - d) / minD) * crowd.sepStrength * 1.6;
        const inv = 1 / d;
        sx += dx * inv * push;
        sy += dy * inv * push;
      }
      return { sx, sy };
    },
    onIntegrate: (a, sx, sy) => {
      a.vx += sx * dt;
      a.vy += sy * dt;
    },
    scratchNear: sepNear,
  });
}

function marchUnit(state: FrontlineState, u: FlUnit): void {
  const { x: bx, y: by } = enemyBasePoint(state, u.team);
  const standOff = u.attackRange + u.radius + C.baseRadius;
  const fromBx = u.x - bx;
  const fromBy = u.y - by;
  let fromLen = Math.hypot(fromBx, fromBy);
  let ringX: number;
  let ringY: number;
  if (fromLen < 1e-3) {
    const back = u.team === 0 ? 1 : -1;
    ringX = bx;
    ringY = by + back * standOff;
  } else {
    ringX = bx + (fromBx / fromLen) * standOff;
    ringY = by + (fromBy / fromLen) * standOff;
  }
  const cell = worldToCell(state, u.x, u.y);
  let laneX = ringX;
  if (cell) {
    laneX = cellCenter(state, cell.col, cell.row).x;
  }
  const aimX = laneX * 0.35 + ringX * 0.65;
  const aimY = ringY;
  const dx = aimX - u.x;
  const dy = aimY - u.y;
  const len = Math.hypot(dx, dy) || 1;
  const spd = u.speed * C.crowd.forwardBias;
  u.intentVx = (dx / len) * spd;
  u.intentVy = (dy / len) * spd;
  if (u.team === 0 && u.intentVy > 0) u.intentVy *= 0.25;
  if (u.team === 1 && u.intentVy < 0) u.intentVy *= 0.25;
  u.vx = u.intentVx;
  u.vy = u.intentVy;
  u.aimAngle = Math.atan2(u.intentVy, u.intentVx);
  u.facing = u.intentVx >= 0 ? 1 : -1;
  u.arrived = false;
}

function aggroDistFor(u: FlUnit): number {
  return Math.max(C.aggroRange, u.attackRange + C.rangedAggroPad);
}

function isRangedUnit(u: FlUnit): boolean {
  return u.attackRange >= C.rangedThreshold;
}

function pickTarget(
  state: FrontlineState,
  u: FlUnit,
  maxDist: number,
): FlUnit | null {
  let best: FlUnit | null = null;
  let bestScore = Infinity;
  const ranged = isRangedUnit(u);
  for (const o of state.units) {
    if (o.team === u.team || o.hp <= 0) continue;
    const dx = o.x - u.x;
    const dy = o.y - u.y;
    const d = Math.hypot(dx, dy);
    if (d > maxDist) continue;
    const ahead = u.team === 0 ? dy < 8 : dy > -8;
    let score = d + (ahead ? 0 : ranged ? 20 : 40);
    const shootRange = u.attackRange + u.radius + o.radius;
    if (ranged && d <= shootRange) score -= 50;
    if (u.tier >= 3 && EVO.t3PreferElite && o.tier >= 2) score -= 35;
    if (score < bestScore) {
      bestScore = score;
      best = o;
    }
  }
  return best;
}

/** Clash-style: while in HQ fire range, never retarget to units. */
export function selectTarget(state: FrontlineState, u: FlUnit): FlUnit | null {
  const { x: bx, y: by } = enemyBasePoint(state, u.team);
  const dHq = Math.hypot(bx - u.x, by - u.y);
  if (dHq <= baseEngageRange(u)) return null;
  return pickTarget(state, u, aggroDistFor(u));
}

function countTier(state: FrontlineState, team: TeamId, tier: FlTier): number {
  let n = 0;
  for (const u of state.units) {
    if (u.team === team && u.tier === tier && u.hp > 0) n++;
  }
  return n;
}

export function sepDistanceFor(a: FlUnit, b: FlUnit): number {
  const crowd = C.crowd;
  const dispA = displayScaleForTier(a.tier);
  const dispB = displayScaleForTier(b.tier);
  return (
    crowd.sepRadius +
    crowd.sepRadiusMul * (a.radius + b.radius) +
    crowd.sepDisplayMul * ((dispA + dispB) * 0.5 - 1)
  );
}

export function killGrowthExcess(kills: number, tier: FlTier): number {
  if (tier <= 1) return Math.max(0, kills);
  if (tier === 2) return Math.max(0, kills - EVO.killsToT2);
  return Math.max(0, kills - EVO.killsToT3);
}

/** Soft-cap kill growth: early kills matter more; converges to 1+growthCap. */
export function killGrowthMul(kills: number, tier: FlTier): number {
  const excess = killGrowthExcess(kills, tier);
  const cap = EVO.growthCap;
  const k = EVO.growthK;
  return 1 + cap * (1 - Math.exp(-k * excess));
}

function applyKillGrowth(u: FlUnit): void {
  const st = statsForUnit(u.tier, u.unitId);
  const mul = killGrowthMul(u.kills, u.tier);
  const ratio = u.maxHp > 0 ? u.hp / u.maxHp : 1;
  u.maxHp = Math.max(1, Math.round(st.hp * mul));
  u.hp = Math.min(u.maxHp, Math.max(1, Math.round(u.maxHp * ratio)));
  u.damage = Math.max(1, Math.round(st.damage * mul));
}

type LineagePools = { t2: string[]; t3: string[] };

function asRolePool(raw: unknown): string[] {
  if (typeof raw === "string" && raw.length > 0) return [raw];
  if (Array.isArray(raw)) {
    return raw.filter((x): x is string => typeof x === "string" && x.length > 0);
  }
  return [];
}

function lineageEntry(lineage: string): LineagePools | null {
  const e = (
    EVO.lineage as Record<string, { t2?: unknown; t3?: unknown } | undefined>
  )[lineage];
  if (!e) return null;
  const t2 = asRolePool(e.t2);
  const t3 = asRolePool(e.t3);
  if (t2.length === 0 || t3.length === 0) return null;
  return { t2, t3 };
}

function pickFromRolePool(state: FrontlineState, pool: string[]): string {
  const i = Math.floor(nextRng(state) * pool.length) % pool.length;
  return pool[i]!;
}

function promoteUnit(state: FrontlineState, u: FlUnit, toTier: 2 | 3): void {
  const lin = lineageEntry(u.lineage);
  if (!lin) return;
  const nextId = pickFromRolePool(state, toTier === 2 ? lin.t2 : lin.t3);
  const fromTier = u.tier as 1 | 2;
  const st = statsForUnit(toTier, nextId);
  const ab = (AB as Record<string, { cd?: number } | undefined>)[nextId];
  u.tier = toTier;
  u.unitId = nextId;
  u.maxHp = st.hp;
  u.hp = st.hp;
  u.damage = st.damage;
  u.radius = st.radius;
  u.baseSpeed = st.speed;
  u.speed = u.surgeT > 0 ? st.speed * (CARDS.surge.speedMul as number) : st.speed;
  u.attackRange = st.attackRange;
  u.attackCdMax = st.attackCd;
  u.attackCd = 0;
  u.abilityCd = ab?.cd ? ab.cd * 0.25 : 0;
  u.level = 1 + u.kills;
  applyKillGrowth(u);
  state.events.push({
    kind: FlEvent.Promote,
    unitId: u.id,
    fromTier,
    toTier,
    role: nextId,
    x: u.x,
    y: u.y,
    kills: u.kills,
    level: u.level,
  });
}

function tryPromoteAfterKill(state: FrontlineState, killer: FlUnit): void {
  killer.level = 1 + killer.kills;
  if (killer.tier === 1 && killer.kills >= EVO.killsToT2) {
    promoteUnit(state, killer, 2);
  } else if (killer.tier === 2 && killer.kills >= EVO.killsToT3) {
    promoteUnit(state, killer, 3);
  } else {
    applyKillGrowth(killer);
  }
}

function tickLaneWaves(state: FrontlineState, dt: number): void {
  for (const team of [0, 1] as const) {
    state.laneCd[team] -= dt;
    if (state.laneCd[team] > 0) continue;
    state.laneCd[team] = C.lane.intervalSec;
    const baseRow = team === 0 ? state.rows - 1 : 0;
    for (let col = 0; col < state.cols; col++) {
      if (!canDeployAt(state, team, col, baseRow)) continue;
      for (let b = 0; b < C.lane.burstPerLane; b++) {
        const c = cellCenter(state, col, baseRow);
        const j = C.lane.jitter;
        const x = c.x + (nextRng(state) * 2 - 1) * j;
        const y = c.y + (nextRng(state) * 2 - 1) * j * 0.5;
        const role = pickFromPool(state, 1);
        const u = spawnUnit(state, team, x, y, 1, role);
        state.events.push({
          kind: FlEvent.Deploy,
          team,
          x,
          y,
          unitId: u.id,
          role,
          tier: 1,
        });
      }
    }
  }
}

function tickReinforceAi(_state: FrontlineState, _dt: number): void {
  // Disabled — reinforcement is card-only (legion / champion).
}

function tickAiCards(state: FrontlineState, dt: number): void {
  const bag = state.cards[1]!;
  bag.aiCastCd -= dt;
  if (bag.aiCastCd > 0) return;
  bag.aiCastCd = SK.ai.castIntervalSec;
  const sid = pickAiCard(bag);
  if (!sid) return;

  const def = CARDS[sid];
  let x = 0;
  let y = 0;
  if (def.target === "cell") {
    let bestCol = -1;
    let bestRow = -1;
    for (let r = 0; r < state.rows; r++) {
      for (let c = 0; c < state.cols; c++) {
        if (!canDeployAt(state, 1, c, r)) continue;
        bestCol = c;
        bestRow = r;
      }
    }
    if (bestCol < 0) return;
    const c = cellCenter(state, bestCol, bestRow);
    x = c.x;
    y = c.y;
  } else if (def.target === "board") {
    x = state.originX + (state.cols * state.cellW) * 0.5;
    y = state.originY + (state.rows * state.cellH) * 0.5;
  } else if (def.target === "enemy_aoe" || def.target === "point_aoe") {
    let best: FlUnit | null = null;
    let bestHp = Infinity;
    for (const u of state.units) {
      if (u.team !== 0 || u.hp <= 0) continue;
      if (u.hp < bestHp) {
        bestHp = u.hp;
        best = u;
      }
    }
    if (!best) return;
    x = best.x;
    y = best.y;
  } else {
    let best: FlUnit | null = null;
    let bestScore = -1;
    for (const u of state.units) {
      if (u.team !== 1 || u.hp <= 0) continue;
      const score = u.kills * 10 + u.tier * 5 + (1 - u.hp / u.maxHp) * 20;
      if (score > bestScore) {
        bestScore = score;
        best = u;
      }
    }
    if (!best) return;
    x = best.x;
    y = best.y;
  }

  tryCastSkill(state, 1, x, y, sid);
}

function applyRawDamage(state: FrontlineState, target: FlUnit, raw: number): number {
  if (target.hp <= 0) return 0;
  if (target.aegisT > 0) return 0;
  let mul = wardenDamageMul(state, target);
  const dmg = Math.max(1, Math.round(raw * mul));
  target.hp -= dmg;
  state.events.push({
    kind: FlEvent.Hit,
    attackerId: 0,
    targetId: target.id,
    x: target.x,
    y: target.y,
    damage: dmg,
    role: "smite",
    x0: target.x,
    y0: target.y,
    x1: target.x,
    y1: target.y,
  });
  if (target.hp <= 0) {
    state.events.push({
      kind: FlEvent.Death,
      unitId: target.id,
      x: target.x,
      y: target.y,
    });
  }
  return dmg;
}

function wardenDamageMul(state: FrontlineState, victim: FlUnit): number {
  let mul = 1;
  for (const a of state.units) {
    if (a.team !== victim.team || a.hp <= 0) continue;
    if (a.unitId !== "fl_t2_warden") continue;
    const ab = AB.fl_t2_warden;
    const d = Math.hypot(a.x - victim.x, a.y - victim.y);
    if (d <= ab.auraRadius) mul *= 1 - ab.damageReduce;
  }
  return mul;
}

function emitBaseHit(
  state: FrontlineState,
  attacker: FlUnit,
  victim: TeamId,
  damage: number,
  x: number,
  y: number,
): void {
  state.baseHp[victim] = Math.max(0, state.baseHp[victim] - damage);
  state.events.push({
    kind: FlEvent.BaseHit,
    team: victim,
    attackerId: attacker.id,
    damage,
    x,
    y,
    role: attacker.unitId,
    x0: attacker.x,
    y0: attacker.y,
    x1: x,
    y1: y,
  });
}

function dealDamage(
  state: FrontlineState,
  attacker: FlUnit,
  target: FlUnit,
  raw: number,
): number {
  if (target.hp <= 0) return 0;
  if (target.aegisT > 0) return 0;
  let mul = wardenDamageMul(state, target);
  const dmg = Math.max(1, Math.round(raw * mul));
  target.hp -= dmg;
  state.events.push({
    kind: FlEvent.Hit,
    attackerId: attacker.id,
    targetId: target.id,
    x: target.x,
    y: target.y,
    damage: dmg,
    role: attacker.unitId,
    x0: attacker.x,
    y0: attacker.y,
    x1: target.x,
    y1: target.y,
  });
  if (target.hp <= 0) {
    state.events.push({
      kind: FlEvent.Death,
      unitId: target.id,
      x: target.x,
      y: target.y,
    });
    onKill(state, attacker, target);
  }
  return dmg;
}

function onKill(state: FrontlineState, killer: FlUnit, dead: FlUnit): void {
  killer.kills += 1;
  tryPromoteAfterKill(state, killer);

  if (killer.unitId !== "fl_t2_reaver") return;
  const ab = AB.fl_t2_reaver;
  killer.hp = Math.min(killer.maxHp, killer.hp + killer.damage * ab.lifesteal);
  for (const o of state.units) {
    if (o.team === killer.team || o.hp <= 0) continue;
    const d = Math.hypot(o.x - dead.x, o.y - dead.y);
    if (d > ab.burstRadius) continue;
    dealDamage(state, killer, o, ab.burstDamage);
  }
  state.events.push({
    kind: FlEvent.Ability,
    abilityId: "spines",
    casterId: killer.id,
    x: dead.x,
    y: dead.y,
    damage: ab.burstDamage,
  });
}

function tickVanguardRam(state: FrontlineState, u: FlUnit): void {
  if (u.unitId !== "fl_t2_vanguard" || u.abilityCd > 0) return;
  const spd = Math.hypot(u.vx, u.vy);
  if (spd < 40) return;
  const ab = AB.fl_t2_vanguard;
  for (const o of state.units) {
    if (o.team === u.team || o.hp <= 0) continue;
    const d = Math.hypot(o.x - u.x, o.y - u.y);
    if (d > ab.ramRadius + u.radius + o.radius) continue;
    dealDamage(state, u, o, ab.ramDamage);
    u.abilityCd = ab.cd;
    state.events.push({
      kind: FlEvent.Ability,
      abilityId: "ram",
      casterId: u.id,
      x: o.x,
      y: o.y,
      targetIds: [o.id],
      damage: ab.ramDamage,
    });
    break;
  }
}

function tickActiveAbility(state: FrontlineState, u: FlUnit): void {
  if (u.tier !== 3 || u.abilityCd > 0) return;
  const ab = (AB as Record<string, { kind?: string; id?: string; cd?: number }>)[
    u.unitId
  ];
  if (!ab || ab.kind !== "active") return;

  if (u.unitId === "fl_t3_colossus") {
    const a = AB.fl_t3_colossus;
    const dmg = u.damage * a.damageMul;
    const hit: number[] = [];
    for (const o of state.units) {
      if (o.team === u.team || o.hp <= 0) continue;
      if (Math.hypot(o.x - u.x, o.y - u.y) > a.radius) continue;
      dealDamage(state, u, o, dmg);
      hit.push(o.id);
    }
    const victim: TeamId = u.team === 0 ? 1 : 0;
    const base = enemyBasePoint(state, u.team);
    if (Math.hypot(base.x - u.x, base.y - u.y) <= a.radius + C.baseRadius) {
      const bd = Math.round(u.damage * a.baseDamageMul);
      emitBaseHit(state, u, victim, bd, base.x, base.y);
    }
    u.abilityCd = a.cd;
    u.attackAnimT = C.attackSwingSec;
    state.events.push({
      kind: FlEvent.Ability,
      abilityId: "shockwave",
      casterId: u.id,
      x: u.x,
      y: u.y,
      targetIds: hit,
      damage: dmg,
    });
    return;
  }

  if (u.unitId === "fl_t3_siege") {
    const a = AB.fl_t3_siege;
    let best: FlUnit | null = null;
    let bestD = a.range;
    for (const o of state.units) {
      if (o.team === u.team || o.hp <= 0) continue;
      const d = Math.hypot(o.x - u.x, o.y - u.y);
      if (d > bestD) continue;
      bestD = d;
      best = o;
    }
    if (!best) {
      const base = enemyBasePoint(state, u.team);
      const d = Math.hypot(base.x - u.x, base.y - u.y);
      if (d <= a.range) {
        const victim: TeamId = u.team === 0 ? 1 : 0;
        const bd = Math.round(u.damage * a.damageMul);
        u.abilityCd = a.cd;
        u.attackAnimT = C.attackSwingSec;
        state.events.push({
          kind: FlEvent.Ability,
          abilityId: "bombard",
          casterId: u.id,
          x: base.x,
          y: base.y,
          damage: bd,
        });
        emitBaseHit(state, u, victim, bd, base.x, base.y);
      }
      return;
    }
    const dmg = u.damage * a.damageMul;
    dealDamage(state, u, best, dmg);
    u.abilityCd = a.cd;
    u.attackAnimT = C.attackSwingSec;
    u.aimAngle = Math.atan2(best.y - u.y, best.x - u.x);
    state.events.push({
      kind: FlEvent.Ability,
      abilityId: "bombard",
      casterId: u.id,
      x: best.x,
      y: best.y,
      targetIds: [best.id],
      damage: dmg,
    });
    return;
  }

  if (u.unitId === "fl_t3_shadow") {
    const a = AB.fl_t3_shadow;
    let best: FlUnit | null = null;
    let bestD = a.range;
    for (const o of state.units) {
      if (o.team === u.team || o.hp <= 0) continue;
      const d = Math.hypot(o.x - u.x, o.y - u.y);
      if (d < 20 || d > bestD) continue;
      bestD = d;
      best = o;
    }
    if (!best) return;
    const ang = Math.atan2(best.y - u.y, best.x - u.x);
    u.x = best.x - Math.cos(ang) * (best.radius + u.radius + 4);
    u.y = best.y - Math.sin(ang) * (best.radius + u.radius + 4);
    const dmg = u.damage * a.damageMul;
    dealDamage(state, u, best, dmg);
    u.abilityCd = a.cd;
    u.attackAnimT = C.attackSwingSec;
    state.events.push({
      kind: FlEvent.Ability,
      abilityId: "blinkstrike",
      casterId: u.id,
      x: best.x,
      y: best.y,
      targetIds: [best.id],
      damage: dmg,
    });
    return;
  }

  if (u.unitId === "fl_t3_storm") {
    const a = AB.fl_t3_storm;
    const enemies = state.units
      .filter((o) => o.team !== u.team && o.hp > 0)
      .map((o) => ({
        o,
        d: Math.hypot(o.x - u.x, o.y - u.y),
      }))
      .filter((e) => e.d <= a.range)
      .sort((x, y) => x.d - y.d);
    if (!enemies.length) return;
    const hit: number[] = [];
    let dmg = u.damage * a.damageMul;
    let cursor = { x: u.x, y: u.y };
    for (let j = 0; j < a.jumps && j < enemies.length; j++) {
      const pick =
        j === 0
          ? enemies[0]!
          : enemies
              .filter((e) => !hit.includes(e.o.id))
              .sort(
                (x, y) =>
                  Math.hypot(x.o.x - cursor.x, x.o.y - cursor.y) -
                  Math.hypot(y.o.x - cursor.x, y.o.y - cursor.y),
              )[0];
      if (!pick) break;
      dealDamage(state, u, pick.o, dmg);
      hit.push(pick.o.id);
      cursor = { x: pick.o.x, y: pick.o.y };
      dmg *= a.jumpFalloff;
    }
    u.abilityCd = a.cd;
    u.attackAnimT = C.attackSwingSec;
    state.events.push({
      kind: FlEvent.Ability,
      abilityId: "chain",
      casterId: u.id,
      x: u.x,
      y: u.y,
      targetIds: hit,
      damage: u.damage * a.damageMul,
    });
    return;
  }

  if (u.unitId === "fl_t3_paladin") {
    const a = AB.fl_t3_paladin;
    u.hp = Math.min(u.maxHp, u.hp + a.selfHeal);
    for (const o of state.units) {
      if (o.team !== u.team || o.hp <= 0) continue;
      if (Math.hypot(o.x - u.x, o.y - u.y) > a.auraRadius) continue;
      o.hp = Math.min(o.maxHp, o.hp + a.heal);
    }
    u.abilityCd = a.cd;
    u.attackAnimT = C.attackSwingSec;
    state.events.push({
      kind: FlEvent.Ability,
      abilityId: "bless",
      casterId: u.id,
      x: u.x,
      y: u.y,
      damage: a.selfHeal,
    });
  }
}

function tickCombat(state: FrontlineState, dt: number): void {
  for (const u of state.units) {
    u.attackCd = Math.max(0, u.attackCd - dt);
    u.abilityCd = Math.max(0, u.abilityCd - dt);
    u.attackAnimT = Math.max(0, u.attackAnimT - dt);
    u.aegisT = Math.max(0, u.aegisT - dt);
    u.surgeT = Math.max(0, u.surgeT - dt);
    u.stunT = Math.max(0, u.stunT - dt);
    if (u.surgeT > 0) {
      u.speed = u.baseSpeed * (CARDS.surge.speedMul as number);
    } else {
      u.speed = u.baseSpeed;
    }
  }

  for (const u of [...state.units]) {
    if (u.hp <= 0) continue;
    if (u.stunT > 0) continue;
    tickVanguardRam(state, u);
    tickActiveAbility(state, u);

    const target = selectTarget(state, u);
    if (target) {
      const d = Math.hypot(target.x - u.x, target.y - u.y);
      u.aimAngle = Math.atan2(target.y - u.y, target.x - u.x);
      if (d <= u.attackRange + u.radius + target.radius) {
        if (u.attackCd <= 0) {
          const cdMul =
            u.surgeT > 0 ? (CARDS.surge.attackCdMul as number) : 1;
          u.attackCd = u.attackCdMax * cdMul;
          u.attackAnimT = C.attackSwingSec;
          dealDamage(state, u, target, u.damage);
        }
      }
      continue;
    }

    const { x: bx, y: by } = enemyBasePoint(state, u.team);
    const bd = Math.hypot(bx - u.x, by - u.y);
    if (bd <= baseEngageRange(u) && u.attackCd <= 0) {
      const cdMul = u.surgeT > 0 ? (CARDS.surge.attackCdMul as number) : 1;
      u.attackCd = u.attackCdMax * cdMul;
      u.attackAnimT = C.attackSwingSec;
      u.aimAngle = Math.atan2(by - u.y, bx - u.x);
      const victim: TeamId = u.team === 0 ? 1 : 0;
      emitBaseHit(state, u, victim, u.damage, bx, by);
    }
  }

  state.units = state.units.filter((u) => u.hp > 0);
}

function countOwned(state: FrontlineState): { ally: number; enemy: number } {
  let ally = 0;
  let enemy = 0;
  for (let i = 0; i < state.owner.length; i++) {
    if (state.owner[i] === 0) ally++;
    else if (state.owner[i] === 1) enemy++;
  }
  return { ally, enemy };
}

function resolveEnd(state: FrontlineState): void {
  if (state.baseHp[1] <= 0) {
    state.phase = "won";
    state.events.push({ kind: FlEvent.Result, result: "won" });
    return;
  }
  if (state.baseHp[0] <= 0) {
    state.phase = "lost";
    state.events.push({ kind: FlEvent.Result, result: "lost" });
    return;
  }
  if (state.timeLeft <= 0) {
    const { ally, enemy } = countOwned(state);
    state.phase = ally > enemy ? "won" : enemy > ally ? "lost" : "draw";
    state.events.push({ kind: FlEvent.Result, result: state.phase });
  }
}

export function drainEvents(state: FrontlineState): FlSimEvent[] {
  const out = state.events;
  state.events = [];
  return out;
}

export function getHudSnapshot(state: FrontlineState): HudSnapshot {
  const owned = countOwned(state);
  let u0 = 0;
  let u1 = 0;
  let topLevel = 1;
  for (const u of state.units) {
    if (u.team === 0) u0++;
    else u1++;
    if (u.team === 0 && u.level > topLevel) topLevel = u.level;
  }
  const bag = state.cards[0]!;
  const cardCosts = {} as Record<FlSkillId, number>;
  const cardExile = {} as Record<FlSkillId, boolean>;
  for (const id of SK.order as FlSkillId[]) {
    cardCosts[id] = CARDS[id].mana;
    cardExile[id] = Boolean((CARDS[id] as { exile?: boolean }).exile);
  }
  return {
    timeLeft: state.timeLeft,
    phase: state.phase,
    selectedSkill: state.selectedSkill,
    selectedHandIndex: state.selectedHandIndex,
    hand: [...bag.hand],
    mana: bag.mana,
    maxMana: SK.economy.maxMana,
    deckCount: bag.deck.length,
    discardCount: bag.discard.length,
    exileCount: bag.exiled.length,
    cardCosts,
    cardExile,
    deployCd: state.deployCd,
    baseHp: [...state.baseHp] as [number, number],
    baseMaxHp: [...state.baseMaxHp] as [number, number],
    ownedAlly: owned.ally,
    ownedEnemy: owned.enemy,
    unitCount: [u0, u1],
    topLevel,
    eliteCount: [
      countTier(state, 0, 2) + countTier(state, 0, 3),
      countTier(state, 1, 2) + countTier(state, 1, 3),
    ],
  };
}

function boardBounds(state: FrontlineState): {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
} {
  return {
    minX: state.originX + 4,
    maxX: state.originX + state.cols * state.cellW - 4,
    minY: state.originY + 4,
    maxY: state.originY + state.rows * state.cellH - 4,
  };
}

/** True when fleeing further along (fx,fy) would push into / past the rim. */
export function retreatBlockedAtEdge(
  state: FrontlineState,
  u: FlUnit,
  fleeX: number,
  fleeY: number,
  pad = 14,
): boolean {
  const b = boardBounds(state);
  if (fleeX < -0.15 && u.x <= b.minX + pad) return true;
  if (fleeX > 0.15 && u.x >= b.maxX - pad) return true;
  if (fleeY < -0.15 && u.y <= b.minY + pad) return true;
  if (fleeY > 0.15 && u.y >= b.maxY - pad) return true;
  return false;
}

function applyEliteMovement(
  state: FrontlineState,
  u: FlUnit,
  target: FlUnit | null,
): boolean {
  // T2 low-HP retreat — cancel when pinned at rim or already far enough to fight.
  if (u.tier === 2 && u.hp / u.maxHp <= EVO.t2RetreatHp && target) {
    const dx = u.x - target.x;
    const dy = u.y - target.y;
    const d = Math.hypot(dx, dy) || 1;
    const safeDist = Math.max(u.attackRange, EVO.t2RetreatDist);
    if (d >= safeDist) return false;
    if (retreatBlockedAtEdge(state, u, dx / d, dy / d)) return false;
    u.intentVx = (dx / d) * u.speed;
    u.intentVy = (dy / d) * u.speed;
    u.vx = u.intentVx;
    u.vy = u.intentVy;
    u.aimAngle = Math.atan2(target.y - u.y, target.x - u.x);
    u.arrived = false;
    return true;
  }
  // Marksman / siege keep preferred range (kite if too close).
  if (
    target &&
    (u.unitId === "fl_t2_marksman" || u.unitId === "fl_t3_siege")
  ) {
    const d = Math.hypot(target.x - u.x, target.y - u.y);
    const ideal = u.attackRange * 0.85;
    if (d < ideal * 0.7) {
      const dx = u.x - target.x;
      const dy = u.y - target.y;
      const len = Math.hypot(dx, dy) || 1;
      if (retreatBlockedAtEdge(state, u, dx / len, dy / len)) return false;
      u.intentVx = (dx / len) * u.speed * 0.85;
      u.intentVy = (dy / len) * u.speed * 0.85;
      u.vx = u.intentVx;
      u.vy = u.intentVy;
      u.aimAngle = Math.atan2(target.y - u.y, target.x - u.x);
      u.arrived = false;
      return true;
    }
    // In band: fall through to holdEngage so they plant and shoot.
  }
  return false;
}

export function tickFrontline(state: FrontlineState, dt: number): void {
  if (state.phase !== "playing") return;
  const t = Math.min(0.05, Math.max(0, dt));
  state.time += t;
  state.timeLeft = Math.max(0, state.timeLeft - t);
  state.deployCd = Math.max(0, state.deployCd - t);

  const rng = { next: () => nextRng(state) };
  for (const team of [0, 1] as const) {
    tickManaAndDraw(state.cards[team]!, t, rng);
  }

  tickLaneWaves(state, t);
  tickReinforceAi(state, t);
  tickAiCards(state, t);
  tickAoeWaves(state, t);

  for (const u of state.units) {
    if (u.stunT > 0) {
      u.intentVx = 0;
      u.intentVy = 0;
      u.vx *= 0.4;
      u.vy *= 0.4;
      continue;
    }
    const target = selectTarget(state, u);
    if (applyEliteMovement(state, u, target)) continue;
    if (target) {
      const d = Math.hypot(target.x - u.x, target.y - u.y);
      const range = u.attackRange + u.radius + target.radius;
      if (d > range) {
        const dx = target.x - u.x;
        const dy = target.y - u.y;
        const len = Math.hypot(dx, dy) || 1;
        u.intentVx = (dx / len) * u.speed;
        u.intentVy = (dy / len) * u.speed;
        u.vx = u.intentVx;
        u.vy = u.intentVy;
        u.aimAngle = Math.atan2(u.intentVy, u.intentVx);
        u.arrived = false;
      } else {
        holdEngage(u, target.x, target.y);
      }
    } else {
      u.arrived = false;
      const { x: bx, y: by } = enemyBasePoint(state, u.team);
      const d = Math.hypot(bx - u.x, by - u.y);
      if (d <= baseEngageRange(u)) {
        holdEngage(u, bx, by);
      } else {
        marchUnit(state, u);
      }
    }
  }

  applySoftSep(state, t);
  // Plant only while holding fire in range (arrived + near-zero intent).
  for (const u of state.units) {
    if (!u.arrived || u.stunT > 0) continue;
    const intentSpd = Math.hypot(u.intentVx, u.intentVy);
    if (intentSpd > C.crowd.walkEnter * 0.35) {
      u.arrived = false;
      continue;
    }
    u.vx *= 0.28;
    u.vy *= 0.28;
  }
  for (const u of state.units) {
    u.x += u.vx * t;
    u.y += u.vy * t;
    const b = boardBounds(state);
    u.x = Math.min(b.maxX, Math.max(b.minX, u.x));
    u.y = Math.min(b.maxY, Math.max(b.minY, u.y));
    paintFoot(state, u);
  }

  tickCombat(state, t);
  resolveEnd(state);
}
